import { Flex, Box, Text, Anchor, ActionIcon, Divider, Grid } from "@mantine/core";
import {
  IconBrandLinkedin,
  IconBrandInstagram,
  IconBrandX,
  IconBrandPinterest,
  IconBrandFacebook,
  IconBrandReddit,
  IconBrandYoutube,
} from "@tabler/icons-react";
import { Outlet, useLocation } from "react-router-dom";
import { Suspense, useEffect } from "react";
import { useMediaQuery } from "@mantine/hooks";
import LandingNavbar, { landingNavbarOffset } from "./LandingNavbar";
import { LandingNavAuthPathsProvider } from "./landingNavAuthPaths.context";
import { PAGE_PADDING_X } from "@/shared/constants/layout.constants";
import { TRUSTPILOT_REVIEW_URL } from "@/shared/constants/trustpilot.const";
import { useAuth } from "@/hooks/useAuth";
import { LandingContentFallback, DiscoverBodySkeleton } from "@/routes/RouteFallback";
import { isGuestExploreListPath } from "@/pages/discover/discoverExplore.const";

type LandingLayoutProps = {
  /** When set, renders instead of the matched child route `<Outlet />`. */
  children?: React.ReactNode;
};

export default function LandingLayout({ children }: LandingLayoutProps = {}) {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const belowSm = useMediaQuery("(max-width: 47.99em)");
  const guestMobileExploreRow = !isAuthenticated && belowSm === true;
  const headerHeight = landingNavbarOffset(guestMobileExploreRow);

  useEffect(() => {
    // Warm profile chunk for list → profile (GuestHome is eager in the route tree).
    void import("@/components/shared/AnonymousCompanyProfile/AnonymousProfile");
  }, []);

  return (
    <LandingNavAuthPathsProvider>
      <Flex direction="column" style={{ minHeight: "100vh" }}>
        <LandingNavbar />

        <Box
          component="main"
          style={{
            flex: 1,
            width: "100%",
            paddingTop: headerHeight,
          }}
        >
          {/* Other lazy guest pages (about, profiles, …). GuestHome is eager — no flash on `/`. */}
          <Suspense
            fallback={
              isGuestExploreListPath(location.pathname) ? (
                <DiscoverBodySkeleton withNavOffset={false} />
              ) : (
                <LandingContentFallback />
              )
            }
          >
            {children ?? <Outlet />}
          </Suspense>
        </Box>

        <Divider color="gray.3" />
        <Box component="footer" bg="white">
          <Box px={PAGE_PADDING_X} style={{ width: "100%", margin: "0 auto", paddingTop: 48, paddingBottom: 16 }}>
            {/* 4-column grid: brand | company | help | legal */}
            <Grid gutter={{ base: 24, sm: 32 }} mb={{ base: 40, sm: 68 }}>
              {/* Brand */}
              <Grid.Col span={{ base: 12, sm: 3 }}>
                <Box ta={{ base: "center", sm: "left" }}>
                  <Anchor href="https://gtsetu.com/" style={{ display: "inline-flex" }}>
                    <img src="/GTFullLogo.png" alt="GTsetu" style={{ height: 100, width: "auto" }} />
                  </Anchor>
                  <Text mt={12} fz={14} fw={400} lh="22.4px" c="#6b7a8d">
                    Trusted, verified global business network for secure collaboration.
                  </Text>
                </Box>
              </Grid.Col>

              {/* Company */}
              <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
                <Box ta="center">
                  <Text fz={14} fw={700} tt="uppercase" lts="0.7px" c="#042f5c" mb={16} lh="22.4px">
                    Company
                  </Text>
                  <Flex direction="column" gap={8} align="center">
                    {[
                      { label: "Our Team", href: "https://gtsetu.com/our-team/" },
                      { label: "About Us", href: "https://gtsetu.com/about/" },
                      { label: "Brochure", href: "https://gtsetu.com/brochure/" },
                      { label: "Reviews", href: "https://gtsetu.com/reviews/" },
                      { label: "Rate us on Trustpilot", href: TRUSTPILOT_REVIEW_URL },
                    ].map(({ label, href }) => (
                      <Anchor key={href} href={href} target="_blank" rel="noopener noreferrer" fz={14} fw={500} c="#042f5c" lh="22.4px" style={{ textDecoration: "none" }}>
                        {label}
                      </Anchor>
                    ))}
                  </Flex>
                </Box>
              </Grid.Col>

              {/* Help */}
              <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
                <Box ta="center">
                  <Text fz={14} fw={700} tt="uppercase" lts="0.7px" c="#1e2a3a" mb={16} lh="22.4px">
                    Help
                  </Text>
                  <Flex direction="column" gap={8} align="center">
                    {[
                      { label: "Support", href: "https://gtsetu.com/support/" },
                      { label: "Resources", href: "https://gtsetu.com/resources/" },
                      { label: "Blogs", href: "https://gtsetu.com/blogs/" },
                      { label: "News", href: "https://gtsetu.com/news/" },
                    ].map(({ label, href }) => (
                      <Anchor key={href} href={href} target="_blank" rel="noopener noreferrer" fz={14} fw={500} c="#042f5c" lh="22.4px" style={{ textDecoration: "none" }}>
                        {label}
                      </Anchor>
                    ))}
                  </Flex>
                </Box>
              </Grid.Col>

              {/* Legal */}
              <Grid.Col span={{ base: 12, xs: 6, sm: 3 }}>
                <Box ta="center">
                  <Text fz={14} fw={700} tt="uppercase" lts="0.7px" c="#1e2a3a" mb={16} lh="22.4px">
                    Legal
                  </Text>
                  <Flex direction="column" gap={8} align="center">
                    {[
                      { label: "Privacy Policy", href: "https://gtsetu.com/privacy/" },
                      { label: "Terms of Service", href: "https://gtsetu.com/terms/" },
                      { label: "Cookie Policy", href: "https://gtsetu.com/cookie-policy/" },
                      { label: "Compliance", href: "https://gtsetu.com/compliance/" },
                    ].map(({ label, href }) => (
                      <Anchor key={href} href={href} target="_blank" rel="noopener noreferrer" fz={14} fw={500} c="#042f5c" lh="22.4px" style={{ textDecoration: "none" }}>
                        {label}
                      </Anchor>
                    ))}
                  </Flex>
                </Box>
              </Grid.Col>
            </Grid>

            <Box mb={24} />

            {/* Bottom bar */}
            <Flex justify="space-between" align="center" wrap="wrap" gap="md" direction={{ base: "column", sm: "row" }}>
              <Text fz={14} c="#002E59">
                @ {new Date().getFullYear()} GTsetu. All rights reserved
              </Text>
              <Flex gap={8}>
                {[
                  {
                    icon: <IconBrandLinkedin size={16} />,
                    bg: "#0077B5",
                    href: "https://www.linkedin.com/company/gt-setu/",
                  },
                  { icon: <IconBrandInstagram size={16} />, bg: "#E1306C", href: "https://www.instagram.com/gtsetu/" },
                  { icon: <IconBrandX size={16} />, bg: "#000000", href: "https://x.com/gt_setu" },
                  { icon: <IconBrandPinterest size={16} />, bg: "#E60023", href: "https://in.pinterest.com/gt_setu/" },
                  { icon: <IconBrandFacebook size={16} />, bg: "#1877F2", href: "https://www.facebook.com/people/GT-Setu/61587851139311/" },
                  { icon: <IconBrandReddit size={16} />, bg: "#FF4500", href: "https://www.reddit.com/user/GT_Setu/" },
                  { icon: <IconBrandYoutube size={16} />, bg: "#FF0000", href: "https://www.youtube.com/@GTSetu" },
                ].map(({ icon, bg, href }) => (
                  <ActionIcon
                    key={href}
                    component="a"
                    href={href}
                    target="_blank"
                    radius="sm"
                    size={32}
                    style={{ background: bg, border: "none" }}
                  >
                    {icon}
                  </ActionIcon>
                ))}
              </Flex>
            </Flex>
          </Box>
        </Box>
      </Flex>
    </LandingNavAuthPathsProvider>
  );
}
